import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";

import {AbstractItem} from "@app/shared/classes/item";
import {Bom, PurchaseRequisitionItem} from "@app/shared/classes/purchase-req";
import {PurchaseChargeCode} from "@app/shared/classes/charge-code";
import {Receipt, ReceiptItem} from "@app/shared/classes/receipt";
import {ReturnItem} from "@app/shared/classes/purchase-return";
import {VendorInvoiceItem} from "@app/shared/classes/vendor-invoice";
import {VendorInvoiceChargeCode} from "@app/shared/classes/vendor-invoice-charge-code";

import {AddressType} from "@app/shared/enums/address-type.enum";
import {Transaction} from "@app/shared/enums/txn-type.enum";

import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {ItemType} from "@app/shared/enums/item-type.enum";
import {CustomerInvoiceItem} from "@app/shared/classes/customer-invoice-item";
import {QueryResult} from "@app/shared/classes/query-result";
import {SelectItem} from "primeng/api";
import {Address, OrgSalesTax, PartnerExternalContact, PartnerPaymentTerms, TradingPartnerInfo} from "@app/shared";

@Injectable()
export class ProcurementService {

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  convertPurchaseReqItemToAbstractItem(prItem: PurchaseRequisitionItem): AbstractItem {
    let abstractItem = new AbstractItem();
    abstractItem.id = prItem.abstractItemId;
    abstractItem.subtype = prItem.subtype;
    abstractItem.fundingType = prItem.fundingType;
    abstractItem.nameOrNumber = prItem.nameOrNumber;
    abstractItem.catalogId = prItem.itemCatalogId;
    abstractItem.catalogName = prItem.itemCatalogName;
    abstractItem.commodityCodeId = prItem.commodityCodeId;
    abstractItem.commodityCodeDescription = prItem.commodityCodeDescription;
    abstractItem.unitOfMeasureId = prItem.unitOfMeasureId;
    abstractItem.unitOfMeasureDescription = prItem.unitOfMeasureDescription;
    abstractItem.purchaseTxnDescription = prItem.description;

    abstractItem.isTaxable = prItem.isTaxable;
    abstractItem.unitCost = prItem.unitCost;

    abstractItem.vendorLaborCategoryId = prItem.vendorLaborCategoryId;
    abstractItem.projectName = prItem.projectName;
    abstractItem.projectLaborCategoryPositionTitle = prItem.projectLaborCategoryPositionTitle;

    return abstractItem;
  }

  convertBomToPurchaseReqItem(bom: Bom): PurchaseRequisitionItem {

    let purchaseItem = new PurchaseRequisitionItem();
    purchaseItem.abstractItemId = bom.abstractItemDetail.id;
    purchaseItem.subtype = bom.abstractItemDetail.subtype;
    purchaseItem.fundingType = bom.abstractItemDetail.fundingType;
    purchaseItem.nameOrNumber = bom.abstractItemDetail.nameOrNumber;
    purchaseItem.itemCatalogId = bom.abstractItemDetail.catalogId;
    purchaseItem.itemCatalogName = bom.abstractItemDetail.catalogName;
    purchaseItem.commodityCodeId = bom.abstractItemDetail.commodityCodeId;
    purchaseItem.commodityCodeDescription = bom.abstractItemDetail.commodityCodeDescription;
    purchaseItem.unitOfMeasureId = bom.abstractItemDetail.unitOfMeasureId;
    purchaseItem.unitOfMeasureDescription = bom.abstractItemDetail.unitOfMeasureDescription;
    purchaseItem.description = bom.abstractItemDetail.purchaseTxnDescription;

    purchaseItem.isTaxable = bom.abstractItemDetail.isTaxable;
    purchaseItem.unitCost = bom.abstractItemDetail.unitCost;
    purchaseItem.salesPrice = bom.abstractItemDetail.salesPrice;

    purchaseItem.vendorLaborCategoryId = bom.abstractItemDetail.vendorLaborCategoryId;
    purchaseItem.projectName = bom.abstractItemDetail.projectName;
    purchaseItem.projectLaborCategoryPositionTitle = bom.abstractItemDetail.projectLaborCategoryPositionTitle;

    return purchaseItem;
  }

  convertCustomerInvoiceItemToPurchaseReqItem(invoiceItem: CustomerInvoiceItem): PurchaseRequisitionItem {

    let purchaseItem = new PurchaseRequisitionItem();
    purchaseItem.nameOrNumber = invoiceItem.nameOrNumber;
    purchaseItem.taxAmount = invoiceItem.taxAmount;
    purchaseItem.isTaxable = invoiceItem.isTaxable;
    if (invoiceItem.subtype == ItemType.Shipping || invoiceItem.subtype == ItemType.Discount) {
      purchaseItem.unitCost = invoiceItem.salesPrice;
    } else {
      purchaseItem.salesPrice = invoiceItem.salesPrice;
    }
    purchaseItem.qtyDelivered = invoiceItem.qtyDelivered;
    purchaseItem.extendedPrice = invoiceItem.extendedPrice;
    purchaseItem.description = invoiceItem.description;
    purchaseItem.unitOfMeasureDescription = invoiceItem.unitOfMeasureDescription;
    purchaseItem.unitOfMeasureId = invoiceItem.unitOfMeasureId;
    purchaseItem.abstractItemId = invoiceItem.abstractItemId;
    purchaseItem.subtype = invoiceItem.subtype;
    purchaseItem.fundingType = invoiceItem.fundingType;
    purchaseItem.internalNotes = invoiceItem.internalNotes;

    return purchaseItem;
  }


  convertVendorInvoiceItemToPurchaseReqItem(invoiceItem: VendorInvoiceItem): PurchaseRequisitionItem {
    let purchaseItem = new PurchaseRequisitionItem();
    purchaseItem.id = invoiceItem.id;
    purchaseItem.abstractItemId = invoiceItem.abstractItemId;

    purchaseItem.subtype = invoiceItem.subtype;
    purchaseItem.fundingType = invoiceItem.fundingType;
    purchaseItem.nameOrNumber = invoiceItem.nameOrNumber;
    purchaseItem.description = invoiceItem.description;
    purchaseItem.unitOfMeasureId = invoiceItem.unitOfMeasureId;
    purchaseItem.unitOfMeasureDescription = invoiceItem.unitOfMeasureDescription;
    purchaseItem.itemCatalogId = invoiceItem.abstractItemDetail.catalogId;
    purchaseItem.itemCatalogName = invoiceItem.abstractItemDetail.catalogName;
    purchaseItem.internalNotes = invoiceItem.internalNotes;

    purchaseItem.isTaxable = invoiceItem.isTaxable;
    purchaseItem.qtyReceived = invoiceItem.qtyReceived;
    purchaseItem.actualUnitCost = invoiceItem.actualUnitPrice;
    purchaseItem.unitCost = invoiceItem.unitCost;
    purchaseItem.taxAmount = invoiceItem.taxAmount;

    purchaseItem.vendorLaborCategoryId = invoiceItem.vendorLaborCategoryId;
    purchaseItem.projectName = invoiceItem.projectName;
    purchaseItem.projectLaborCategoryPositionTitle = invoiceItem.projectLaborCategoryPositionTitle;

    purchaseItem.purchaseChargeCodes = this.convertInvoiceChargeCodeToPurchaseChargeCode(invoiceItem.invoiceChargeCodes);
    return purchaseItem;
  }

  convertInvoiceChargeCodeToPurchaseChargeCode(vendorInvoiceChargeCodes: VendorInvoiceChargeCode[]): PurchaseChargeCode[] {
    let purchaseChargeCodes = null;
    if (vendorInvoiceChargeCodes) {
      purchaseChargeCodes = new Array<PurchaseChargeCode>();
      vendorInvoiceChargeCodes.forEach(viCC => {
        let pCC = new PurchaseChargeCode();
        pCC.chargeCodeId = viCC.chargeCodeId;
        pCC.allocationAmount = viCC.allocationAmount;
        pCC.allocationPercent = viCC.allocationPercent;
        pCC.taxAmount = viCC.taxAmount;
        pCC.chargeNumber = viCC.chargeNumber;
        pCC.chargeCodeOwnerName = viCC.chargeCodeOwnerName;
        purchaseChargeCodes.push(pCC);
      });
    }
    return purchaseChargeCodes;
  }

  convertPurchaseChargeCodeToVendorInvoiceChargeCode(purchaseChargeCodes: PurchaseChargeCode[]): VendorInvoiceChargeCode[] {
    let invoiceChargeCodes = null;
    if (purchaseChargeCodes) {
      invoiceChargeCodes = new Array<VendorInvoiceChargeCode>();
      purchaseChargeCodes.forEach(piCC => {
        let invCC = new VendorInvoiceChargeCode();
        invCC.chargeCodeId = piCC.chargeCodeId;
        invCC.allocationAmount = piCC.allocationAmount;
        invCC.allocationPercent = piCC.allocationPercent;
        invCC.taxAmount = piCC.taxAmount;
        invCC.chargeNumber = piCC.chargeNumber;
        invCC.chargeCodeOwnerName = piCC.chargeCodeOwnerName;
        invoiceChargeCodes.push(invCC);
      });
    }
    return invoiceChargeCodes;
  }

  convertToCustomerInvoiceItem(prItem: PurchaseRequisitionItem): CustomerInvoiceItem {
    let invoiceItem = new CustomerInvoiceItem();
    invoiceItem.id = prItem.id;
    invoiceItem.abstractItemId = prItem.abstractItemId;

    invoiceItem.subtype = prItem.subtype;
    invoiceItem.fundingType = prItem.fundingType;
    invoiceItem.nameOrNumber = prItem.nameOrNumber;
    invoiceItem.description = prItem.description;
    invoiceItem.unitOfMeasureId = prItem.unitOfMeasureId;
    invoiceItem.unitOfMeasureDescription = prItem.unitOfMeasureDescription;
    invoiceItem.internalNotes = prItem.internalNotes;

    invoiceItem.isTaxable = prItem.isTaxable;
    invoiceItem.qtyOrdered = prItem.qtyOrdered;
    invoiceItem.salesPrice = prItem.salesPrice;
    invoiceItem.taxAmount = prItem.taxAmount;
    invoiceItem.extendedPrice = prItem.extendedPrice;
    invoiceItem.qtyDelivered = prItem.qtyDelivered;
    if (prItem.qtyOrdered) {
      invoiceItem.qtyOrdered = prItem.qtyOrdered;
    }
    if (prItem.qtyBackOrdered) {
      invoiceItem.qtyBackOrdered = prItem.qtyBackOrdered;
    }
    invoiceItem.abstractItemDetail = this.convertPurchaseReqItemToAbstractItem(prItem);
    return invoiceItem;
  }

  convertToVendorInvoiceItem(prItem: PurchaseRequisitionItem): VendorInvoiceItem {
    let invoiceItem = new VendorInvoiceItem();
    invoiceItem.id = prItem.id;
    invoiceItem.abstractItemId = prItem.abstractItemId;

    invoiceItem.subtype = prItem.subtype;
    invoiceItem.fundingType = prItem.fundingType;
    invoiceItem.nameOrNumber = prItem.nameOrNumber;
    invoiceItem.description = prItem.description;
    invoiceItem.unitOfMeasureId = prItem.unitOfMeasureId;
    invoiceItem.unitOfMeasureDescription = prItem.unitOfMeasureDescription;
    invoiceItem.internalNotes = prItem.internalNotes;

    invoiceItem.isTaxable = prItem.isTaxable;
    invoiceItem.qtyOrdered = prItem.qtyOrdered;
    invoiceItem.qtyReceived = prItem.qtyReceived;
    invoiceItem.unitCost = prItem.unitCost;
    invoiceItem.actualUnitPrice = prItem.actualUnitCost;
    invoiceItem.extendedPrice = prItem.extendedPrice;
    invoiceItem.taxAmount = prItem.taxAmount;

    invoiceItem.abstractItemDetail = this.convertPurchaseReqItemToAbstractItem(prItem);

    invoiceItem.vendorLaborCategoryId = prItem.vendorLaborCategoryId;
    invoiceItem.projectName = prItem.projectName;
    invoiceItem.projectLaborCategoryPositionTitle = prItem.projectLaborCategoryPositionTitle;

    invoiceItem.invoiceChargeCodes = this.convertPurchaseChargeCodeToVendorInvoiceChargeCode(prItem.purchaseChargeCodes);

    return invoiceItem;
  }


  convertReceiptItemToPurchaseReqItem(receiptItem: ReceiptItem): PurchaseRequisitionItem {
    let purchaseItem = new PurchaseRequisitionItem();
    purchaseItem.id = receiptItem.id;
    purchaseItem.abstractItemId = receiptItem.abstractItemId;

    purchaseItem.subtype = receiptItem.subtype;
    purchaseItem.fundingType = receiptItem.fundingType;
    purchaseItem.nameOrNumber = receiptItem.nameOrNumber;
    purchaseItem.description = receiptItem.description;
    purchaseItem.unitOfMeasureId = receiptItem.unitOfMeasureId;
    purchaseItem.unitOfMeasureDescription = receiptItem.unitOfMeasureDescription;
    purchaseItem.itemCatalogId = receiptItem.abstractItemDetail.catalogId;
    purchaseItem.itemCatalogName = receiptItem.abstractItemDetail.catalogName;
    purchaseItem.internalNotes = receiptItem.internalNotes;

    purchaseItem.isTaxable = receiptItem.isTaxable;
    purchaseItem.qtyReceived = receiptItem.qtyReceived;
    purchaseItem.unitCost = receiptItem.abstractItemDetail.unitCost;

    return purchaseItem;
  }


  convertToReceiptItem(prItem: PurchaseRequisitionItem): ReceiptItem {
    let receiptItem = new ReceiptItem();
    receiptItem.id = prItem.id;
    receiptItem.abstractItemId = prItem.abstractItemId;
    receiptItem.subtype = prItem.subtype;
    receiptItem.fundingType = prItem.fundingType;
    receiptItem.nameOrNumber = prItem.nameOrNumber;
    receiptItem.description = prItem.description;
    receiptItem.unitOfMeasureId = prItem.unitOfMeasureId;
    receiptItem.unitOfMeasureDescription = prItem.unitOfMeasureDescription;
    receiptItem.internalNotes = prItem.internalNotes;

    receiptItem.qtyOrdered = prItem.qtyOrdered;
    receiptItem.qtyReceived = prItem.qtyReceived;

    receiptItem.abstractItemDetail = this.convertPurchaseReqItemToAbstractItem(prItem);

    return receiptItem;
  }


  convertReturnItemToPurchaseReqItem(returnItem: ReturnItem): PurchaseRequisitionItem {
    let purchaseItem = new PurchaseRequisitionItem();
    purchaseItem.id = returnItem.id;
    //purchaseItem.abstractItemId = returnItem.abstractItemId;
    //purchaseItem.qtyReceived = returnItem.qtyReceived;
    //purchaseItem.internalNotes = returnItem.internalNotes;

    purchaseItem.subtype = returnItem.subtype;
    purchaseItem.fundingType = returnItem.fundingType;
    purchaseItem.nameOrNumber = returnItem.nameOrNumber;
    purchaseItem.description = returnItem.description;
    purchaseItem.unitOfMeasureId = returnItem.unitOfMeasureId;
    purchaseItem.unitOfMeasureDescription = returnItem.unitOfMeasureDescription;
    purchaseItem.itemCatalogId = returnItem.abstractItemDetail.catalogId;
    purchaseItem.itemCatalogName = returnItem.abstractItemDetail.catalogName;

    purchaseItem.unitCost = returnItem.unitCost;

    return purchaseItem;
  }


  convertToReturnItem(prItem: PurchaseRequisitionItem): ReturnItem {
    let returnItem = new ReturnItem();
    returnItem.id = prItem.id;

    returnItem.subtype = prItem.subtype;
    returnItem.nameOrNumber = prItem.nameOrNumber;
    returnItem.description = prItem.description;
    returnItem.unitOfMeasureId = prItem.unitOfMeasureId;
    returnItem.unitOfMeasureDescription = prItem.unitOfMeasureDescription;

    returnItem.qtyOrdered = prItem.qtyOrdered;
    returnItem.qtyReceived = prItem.qtyReceived;
    //returnItem.internalNotes = prItem.internalNotes;
    returnItem.abstractItemDetail = this.convertPurchaseReqItemToAbstractItem(prItem);

    return returnItem;
  }



  getVendorInvoiceById(invoiceId: number) {
    let url = `/accounts-payable/invoices/${invoiceId}`;

    return this.http.get<any>(url);
  }


  searchVendorInvoiceSummary(purchaseOrderId: number, vendorId: number): Observable<any> {
    let url = `/accounts-payable/invoices/search?vendorId=${vendorId}&purchaseOrderId=${purchaseOrderId}`;

    return this.http.get<any>(url);
  }


  searchVendorInvoice(purchaseOrderId: number, vendorId: number, excludePayment: boolean): Observable<any> {
    let url = `/accounts-payable/invoices/search?purchaseOrderId=${purchaseOrderId}`;

    if (vendorId) {
      url += `vendorId=${vendorId}&`;
    }
    if (purchaseOrderId) {
      url += `purchaseOrderId=${purchaseOrderId}&`;
    }
    if (excludePayment) {
      url += `excludePayment=${excludePayment}`;
    }
    return this.http.get<any>(url);
  }

  getPurchaseOrderInfoById(poId: number): Observable<any> {
    let url = `/procurement/purchase-orders/${poId}/info`;

    return this.http.get<any>(url);
  }

  searchPurchaseOrderInfo(poNumber: string, vendorId: number): Observable<QueryResult> {
    let url = `/procurement/purchase-orders/info/search?purchaseOrderNumber=${poNumber}&vendorId=${vendorId}&start=0&count=-1`;

    return this.http.get<QueryResult>(url);
  }


  getReceiptById(receiptId: number): Observable<Receipt> {
    let url = `/procurement/receipts/${receiptId}`;
    return this.http.get<Receipt>(url);
  }


  searchReceipt(purchaseOrderId: number, vendorId: number, txnType: Transaction): Observable<QueryResult> {
    const url = `/procurement/receipts/search?vendorId=${vendorId}&purchaseOrderId=${purchaseOrderId}&transaction=${txnType}`;
    return this.http.get<QueryResult>(url);
  }


  searchReceiptInfoNoPO(vendorId: number, referenceNumberOrReceiptNumber: string): Observable<QueryResult> {
    const url = `/procurement/receipts-info/search?vendorId=${vendorId}&referenceNumberOrReceiptNumber=${referenceNumberOrReceiptNumber}&noPO=true`;
    return this.http.get<QueryResult>(url);
  }


  getPurchaseReturnById(returnId: number) {
    let url = `/procurement/purchase-returns/${returnId}`;

    return this.http.get<any>(url);
  }


  getPartnerPaymentTerms(partnerId: number): Observable<PartnerPaymentTerms> {
    const url = `/procurement/trading-partners/${partnerId}/payment-terms`;

    return this.http.get<PartnerPaymentTerms>(url);
  }

  getPurchaseSalesTax(shipToId: number, vendorId: number): Observable<OrgSalesTax> {
    const url = `/procurement/sales-taxes/purchase-tax-rates?shipToId=${shipToId}&vendorId=${vendorId}`;
    return this.http.get<OrgSalesTax>(url);
  }

  getPurchaseSalesTaxValue(shipToId: number, vendorId: number): Observable<number> {
    let url = `/procurement/sales-taxes/purchase-tax-rates/value?shipToId=${shipToId}&vendorId=${vendorId}`;
    return this.http.get<number>(url);
  }

  getTradingPartnerById(tradingPartnerId: number): Observable<any> {
    const url = `/procurement/trading-partners/${tradingPartnerId}?`;

    return this.http.get<any>(url);
  }

  getTradingPartnerInfoById(partnerId: number): Observable<TradingPartnerInfo> {
    let url = `/procurement/trading-partners/${partnerId}/info?`;

    return this.http.get<TradingPartnerInfo>(url);
  }


  getTradingPartnerContactAddressById(partnerId: number, addressId: number): Observable<Address> {
    let url = `/procurement/trading-partners/${partnerId}/addresses/${addressId}?`;

    return this.http.get<Address>(url);
  }


  getTradingPartnerContacts(partnerId: number, contactType?: string): Observable<QueryResult> {
    let url = `/procurement/trading-partners/${partnerId}/contacts?`;

    if (contactType) {
      url += `contactType=${contactType}`;
    }
    return this.http.get<QueryResult>(url);
  }


  getTradingPartnerContactsAsDropdown(partnerId: number, contactType?: string, idAsValue?: boolean): Observable<SelectItem[]> {
    return this.getTradingPartnerContacts(partnerId, contactType).pipe(map(res => {
      if (idAsValue) {
        return <SelectItem<number>[]>this.transform.convertToDropDown("fullName", res.list, "id", true);
      } else {
        return <SelectItem<PartnerExternalContact>[]>this.transform.convertToDropDown("fullName", res.list, null, true);
      }
    }));
  }

  //	MAIN, CONTACT, SHIP_TO, BILL_TO, SHIP_FROM, BILL_FROM, RETURN_TO, DBA, OTHER, WAREHOUSE
  getTradingPartnerAddress(partnerId: number, addrType?: AddressType): Observable<QueryResult> {
    let url = `/procurement/trading-partners/addresses/search?partner.id=${partnerId}&addrType=${addrType}`;

    return this.http.get<QueryResult>(url);
  }

  getTradingPartnerAddressAsDropdown(partnerId: number, addrType?: AddressType, idAsValue?: boolean): Observable<SelectItem[]> {
    return this.getTradingPartnerAddress(partnerId, addrType).pipe(map(res => {
      if (idAsValue) {
        return <SelectItem<number>[]>this.transform.convertToDropDown("addrName", res.list, "id", true);
      } else {
        return <SelectItem<Address>[]>this.transform.convertToDropDown("addrName", res.list, null, true);
      }
    }));
  }
}
